/* eslint-disable react/no-unescaped-entities */
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useApp } from "../contexts/AppContext";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { postOrQueue } from "../lib/offline";
import { speak } from "../lib/i18n";
import {
  HandHelping, Apple, Droplet, ShowerHead, Bed, Pill, Check, X, ThumbsUp, Volume2, Send, Eraser,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { VoiceRecorder, VoiceBubble } from "../components/VoiceMessage";

const WORDS = [
  { key: "pain", label: "Douleur", Icon: HandHelping, color: "#D9534F" },
  { key: "hunger", label: "Faim", Icon: Apple, color: "#D4AF5A" },
  { key: "thirst", label: "Soif", Icon: Droplet, color: "#3a8acb" },
  { key: "toilet", label: "Toilettes", Icon: ShowerHead, color: "#0A1426" },
  { key: "tired", label: "Fatigué", Icon: Bed, color: "#8a6e9a" },
  { key: "med", label: "Médicament", Icon: Pill, color: "#2BA3A0" },
  { key: "yes", label: "Oui", Icon: Check, color: "#5CB85C" },
  { key: "no", label: "Non", Icon: X, color: "#D9534F" },
  { key: "thanks", label: "Merci", Icon: ThumbsUp, color: "#D4AF5A" },
  { key: "help", label: "Aide", Icon: HandHelping, color: "#2BA3A0" },
];

function WordsTab({ lang }) {
  const [picked, setPicked] = useState([]);
  return (
    <div className="flex flex-col gap-4 pt-2">
      <div className="bg-white rounded-3xl border border-[#E4E0D5] p-4 min-h-[100px]">
        <div className="text-[11px] uppercase tracking-wider text-[#0A1426]/60 font-bold">Phrase</div>
        <div className="text-[20px] font-[Fraunces] font-bold text-[#0A1426] mt-1 min-h-[28px]">
          {picked.length ? picked.map((w) => w.label).join(" ") : "Touchez les images pour former une phrase."}
        </div>
        <div className="flex gap-2 mt-3">
          <button
            data-testid="word-announce-btn"
            disabled={!picked.length}
            onClick={() => speak(picked.map((w) => w.label).join(" "), lang)}
            className="flex-1 h-12 rounded-full bg-[#2BA3A0] text-white font-bold flex items-center justify-center gap-2 disabled:opacity-40"
          >
            <Volume2 size={18} /> Annoncer
          </button>
          <button
            data-testid="word-clear-btn"
            onClick={() => setPicked([])}
            className="w-12 h-12 rounded-full bg-[#F5F3EE] text-[#0A1426] grid place-items-center"
          >
            <Eraser size={18} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {WORDS.map((w) => {
          const I = w.Icon;
          return (
            <button
              key={w.key}
              data-testid={`word-${w.key}`}
              onClick={() => { setPicked((p) => [...p, w]); speak(w.label, lang); }}
              className="bg-white rounded-2xl border border-[#E4E0D5] p-4 flex flex-col items-center gap-2 hover:border-[#2BA3A0] transition-colors min-h-[120px]"
            >
              <span
                className="w-16 h-16 grid place-items-center rounded-2xl"
                style={{ backgroundColor: `${w.color}1f`, color: w.color }}
              >
                <I size={32} strokeWidth={2.2} />
              </span>
              <span className="font-bold text-[#0A1426] text-base">{w.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function MessagesTab() {
  const { user } = useAuth();
  const { patient } = useApp();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const bottomRef = useRef(null);

  // Determine thread + destinataire (therapist if patient/aidant, else patient user)
  const therapistId = patient?.therapeute_id;
  const patientUserId = patient?.user_id;
  const isCareTeamUser = user?.role === "therapeute";
  const destinataireId = isCareTeamUser ? patientUserId : therapistId;
  const threadId = patientUserId && therapistId ? `${patientUserId}__${therapistId}` : null;

  const load = async () => {
    if (!threadId) return;
    const { data } = await api.get(`/messages?thread_id=${threadId}`);
    setMessages(data);
  };
  useEffect(() => { load(); }, [threadId]); // eslint-disable-line
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!text.trim() || !threadId || !destinataireId) return;
    const body = { thread_id: threadId, destinataire_id: destinataireId, texte: text.trim() };
    setText("");
    const r = await postOrQueue("/messages", body);
    if (r._queued) {
      setMessages((m) => [...m, { ...body, expediteur_id: user.id, statut: "file", date: new Date().toISOString(), id: `tmp-${Date.now()}` }]);
    } else {
      load();
    }
  };

  return (
    <div className="flex flex-col gap-3 pt-2">
      <div className="bg-white rounded-3xl border border-[#E4E0D5] p-3 min-h-[300px] max-h-[55vh] overflow-y-auto bc-scroll">
        {messages.length === 0 && (
          <div className="text-center text-sm text-[#0A1426]/60 py-10">Aucun message pour l'instant.</div>
        )}
        {messages.map((m) => {
          const mine = m.expediteur_id === user.id;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"} my-1.5`}>
              {m.type === "vocal" ? <VoiceBubble msg={m} mine={mine} onRefresh={load} /> : (
              <div
                data-testid={`msg-${mine ? "mine" : "them"}`}
                className={`max-w-[80%] px-3.5 py-2 rounded-2xl text-[14px] leading-snug ${
                  mine ? "bg-[#2BA3A0] text-white rounded-br-md" : "bg-[#F5F3EE] text-[#0A1426] rounded-bl-md"
                }`}
              >
                {m.texte}
                {m.statut === "file" && <span className="block text-[10px] mt-1 opacity-80">En file…</span>}
              </div>
              )}
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>
      <div className="flex items-center gap-2">
        <VoiceRecorder threadId={threadId} destinataireId={destinataireId}
          onSent={(res, duree) => res._queued
            ? setMessages((m) => [...m, { id: `tmp-${Date.now()}`, type: "vocal", duree_s: duree, expediteur_id: user.id, statut: "file", date: new Date().toISOString() }])
            : load()} />
        <input
          data-testid="msg-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") send(); }}
          placeholder="Écrire à mon équipe…"
          className="flex-1 h-14 px-4 rounded-full bg-white border border-[#E4E0D5] text-[#0A1426] focus:outline-none focus:ring-2 focus:ring-[#2BA3A0]"
        />
        <button
          data-testid="msg-send-btn"
          onClick={send}
          className="w-14 h-14 grid place-items-center rounded-full bg-[#2BA3A0] text-white"
        >
          <Send size={22} />
        </button>
      </div>
      <p data-testid="voice-hint" className="text-[11px] text-[#0A1426]/60 text-center">Pas besoin d'écrire : appuyez sur le micro et parlez, votre équipe écoutera votre message.</p>
    </div>
  );
}

export default function CommunicatePage() {
  const { lang } = useApp();
  const loc = useLocation();
  const initial = loc.state?.tab || "words";

  return (
    <div className="bc-in pt-2 pb-6">
      <Tabs defaultValue={initial}>
        <TabsList className="grid grid-cols-2 bg-white rounded-full p-1 border border-[#E4E0D5] h-12">
          <TabsTrigger data-testid="tab-words" value="words" className="rounded-full data-[state=active]:bg-[#2BA3A0] data-[state=active]:text-white font-bold">
            Carnet de mots
          </TabsTrigger>
          <TabsTrigger data-testid="tab-messages" value="messages" className="rounded-full data-[state=active]:bg-[#2BA3A0] data-[state=active]:text-white font-bold">
            Messages
          </TabsTrigger>
        </TabsList>
        <TabsContent value="words"><WordsTab lang={lang} /></TabsContent>
        <TabsContent value="messages"><MessagesTab /></TabsContent>
      </Tabs>
    </div>
  );
}
